import React, { useState } from 'react'
import { Alert, AlertDescription } from '../ui/alert'
import { changePassword, verifyPassword } from '../../lib/api/api'
import { useToast } from '../../hooks/use-toast'
import { PasswordForm } from './password-form'

interface ChangePasswordFormProps {
  onSuccess?: () => void
}

export function ChangePasswordForm({ onSuccess }: ChangePasswordFormProps) {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)
  const { toast } = useToast()

  const onSubmit = async (data: { currentPassword: string; newPassword: string }) => {
    setIsLoading(true)
    setError('')
    setSuccess(false)

    try {
      // First verify the current password
      const verifyResult = await verifyPassword(data.currentPassword)
      
      if (!verifyResult.verified) {
        setError('Current password is incorrect')
        setIsLoading(false)
        return
      }

      // Then change the password
      const result = await changePassword(data.currentPassword, data.newPassword)
      
      if (result.success) {
        setSuccess(true)
        toast({
          title: 'Password Changed Successfully',
          description: 'Your password has been updated.',
          duration: 3000, // Show for 3 seconds
        })
        if (onSuccess) {
          onSuccess()
        }
      } else {
        setError(result.message || 'Failed to change password')
        toast({
          title: 'Error',
          description: result.message || 'Failed to change password',
          variant: 'destructive',
        })
      }
    } catch (err: any) {
      const errorMessage = err?.response?.data?.message || err.message || 'An error occurred'
      setError(errorMessage)
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      })
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-bold text-[#CECFD2]">Change Password</h3>
        <p className="text-sm text-[#CECFD2]">
          Update your password to keep your account secure.
        </p>
      </div>

      {error && (
        <Alert variant="destructive" className="border border-red-700 bg-[#26222E] text-red-400">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {success && (
        <Alert className="border border-green-700 bg-[#26222E] text-green-400 p-4 rounded-md">
          <AlertDescription>
            <p className="font-medium">Your password has been changed successfully!</p>
          </AlertDescription>
        </Alert>
      )}

      <PasswordForm
        showCurrentPassword={true}
        onSubmit={onSubmit}
        isLoading={isLoading}
        submitButtonText="Update Password"
        loadingButtonText="Updating..."
      />
    </div>
  )
}